// RecordsPage.js
import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import PageHero from './PageHero';
import API_CONFIG from '../config/api';
import { getAllCMSImages } from '../services/s3Service';
import { getFirstCachedImage } from '../utils/cmsCache';
import { SkeletonRecordsTable } from './Skeleton';
import '../styles/records/RecordsPage.css';

// Normaliza un ítem de récord para que la vista siempre use los mismos campos (API puede enviar snake_case, camelCase o nombres de BD).
function normalizeRecord(r) {
  if (!r || typeof r !== 'object') return r;
  const getTipo = () => {
    const t = (r.tipo_record ?? r.tipoRecord ?? r.tipo_record_label ?? r.type ?? '').toString().trim();
    return t.toLowerCase().replace(/\s+/g, '_');
  };
  const getValor = () => {
    const v = r.valor ?? r.value ?? r.valorKg;
    if (v === undefined || v === null || v === '') return null;
    const n = Number(String(v).replace(',', '.'));
    return Number.isNaN(n) ? null : n;
  };
  const getSexo = () => {
    const s = String(r.sexo ?? r.division ?? r.genero ?? '').trim().toLowerCase();
    if (s.includes('fem') || s.includes('mujer') || s.includes('dama') || s === 'femenino' || s === 'f') return 'femenino';
    if (s.includes('masc') || s.includes('varon') || s.includes('hombre') || s.includes('masculino') || s === 'm') return 'masculino';
    return 'masculino'; // por defecto si viene vacío o ambiguo
  };
  const tipo = getTipo();
  const tipoLabel = r.tipo_record_label ?? r.tipoRecordLabel ?? (tipo === 'squat' ? 'Squat' : tipo === 'bench_press' || tipo === 'bp' ? 'Bench Press' : tipo === 'deadlift' || tipo === 'dl' ? 'Deadlift' : tipo === 'total' ? 'Total' : tipo || '');
  const edadStr = (r.edad ?? r.categoria_edad ?? 'open').toString().trim() || 'open';
  return {
    ...r,
    id: r.id,
    tipo_record: tipo,
    tipo_record_label: tipoLabel,
    valor: getValor(),
    kg: r.kg ?? getValor(),
    nombre: (r.nombre ?? r.nombre_atleta ?? r.nombreAtleta ?? '—').toString().trim() || '—',
    nombre_atleta: (r.nombre_atleta ?? r.nombre ?? r.nombreAtleta ?? '—').toString().trim() || '—',
    sexo: getSexo(),
    edad: edadStr,
    categoria_edad: (r.categoria_edad ?? r.edad ?? 'open').toString().trim() || 'open',
    peso: (r.peso ?? r.categoria_peso ?? '—').toString().trim() || '—',
    modalidad: (r.modalidad ?? 'raw').toString().trim() || 'raw',
    tipo: (r.tipo ?? r.tipo_ambito ?? 'otro').toString().trim().toLowerCase(),
    tipoCompetencia: (r.tipoCompetencia ?? r.tipo_competencia ?? 'untested').toString().trim().toLowerCase(),
    fecha: r.fecha ?? r.fecha_record ?? null,
    fecha_record: (r.fecha_record ?? r.fecha ?? '').toString().trim() || null,
    lugar: (r.lugar ?? '—').toString().trim() || '—',
  };
}

// Orden y títulos de la tabla (columnas visibles; sin id; títulos sin guion bajo)
const RECORDS_COLUMNS_VISIBLE = [
  { key: 'categoria_peso', label: 'categoria peso' },
  { key: 'tipo_record', label: 'tipo record' },
  { key: 'kg', label: 'kg' },
  { key: 'nombre_atleta', label: 'nombre atleta' },
  { key: 'lugar', label: 'lugar' },
  { key: 'fecha_record', label: 'fecha record' },
  { key: 'division', label: 'division' },
  { key: 'categoria_edad', label: 'CATEGORIA EDAD' },
  { key: 'modalidad', label: 'modalidad' },
  { key: 'tipo_competencia', label: 'tipo competencia' },
];

// Fecha "sin especificar" que usa el import cuando el Excel no trae fecha; no mostrarla como fecha real.
const FECHA_NO_DISPONIBLE_STR = '1900-01-01';

function formatFecha(fecha) {
  if (fecha == null || fecha === '') return '—';
  const s = String(fecha).trim();
  if (s.startsWith('1900-01-01') || s === FECHA_NO_DISPONIBLE_STR) return '—';
  const match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (match) {
    const [, year, month, day] = match;
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('es-ES');
  }
  try {
    const d = new Date(fecha);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-ES');
  } catch {
    return '—';
  }
}

function labelTipo(t) {
  if (t == null || t === '') return '—';
  const s = String(t).trim().toLowerCase();
  if (s === 'squat') return 'Squat';
  if (s === 'bench_press' || s === 'bp') return 'Bench Press';
  if (s === 'deadlift' || s === 'dl') return 'Deadlift';
  if (s === 'total') return 'Total';
  return t;
}

// Por cada columna, solo se usa su campo (o alias conocido). Sin cruces entre columnas.
// nombre_atleta: priorizar nombre (alias que envía el BFF) y descartar valores que parezcan fecha.
function looksLikeDate(s) {
  if (typeof s !== 'string' || !s.trim()) return false;
  return /^\d{4}-\d{2}-\d{2}/.test(s.trim()) || /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(s.trim()) || /^(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\s+\d{4}/i.test(s.trim());
}

function getRecordValue(record, key) {
  if (key === 'nombre_atleta') {
    const candidates = [record.nombre_atleta, record.nombre, record.nombreAtleta];
    for (const v of candidates) {
      if (v === undefined || v === null) continue;
      const s = String(v).trim();
      if (s === '' || looksLikeDate(s)) continue;
      return s;
    }
    return undefined;
  }
  const sources = {
    id: [record.id],
    categoria_peso: [record.categoria_peso, record.peso],
    tipo_record: [record.tipo_record],
    kg: [record.kg, record.valor, record.value],
    lugar: [record.lugar],
    fecha_record: [record.fecha_record, record.fecha],
    division: [record.division],
    modalidad: [record.modalidad],
    categoria_edad: [record.categoria_edad, record.edad],
    tipo_competencia: [record.tipoCompetencia, record.tipo_competencia],
  };
  const list = sources[key];
  if (!list) return record[key];
  for (const v of list) {
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

function formatRecordCell(record, key) {
  const raw = getRecordValue(record, key);
  if (raw === undefined || raw === null || raw === '') return '—';
  if (key === 'kg') {
    const n = Number(raw);
    return Number.isNaN(n) ? '—' : (Number.isInteger(n) ? String(n) : Number(n).toFixed(1));
  }
  // fecha_record: mostrar tal como en la BD (mes y año solamente), sin reformatear
  if (key === 'fecha_record') {
    const s = String(raw).trim();
    if (!s || s.startsWith('1900-01-01')) return '—';
    const m = s.match(/^(.*?)[\s,]+(\d{4})$/);
    if (!m) return s;
    const mes = (m[1] || '').trim();
    const anio = (m[2] || '').trim();
    if (!mes || !anio) return s;
    return (
      <span className="records-fecha-record">
        <span className="records-fecha-record-mes">{mes}</span>
        <br />
        <span className="records-fecha-record-anio">{anio}</span>
      </span>
    );
  }
  if (key === 'tipo_record') return labelTipo(record.tipo_record_label || raw);
  if (key === 'categoria_edad') return String(raw).trim().replace(/_/g, ' ').toUpperCase() || '—';
  if (key === 'modalidad') {
    const m = String(raw).trim().toLowerCase().replace(/_/g, ' ');
    if (m === 'classic raw' || m === 'classic_raw') return 'Classic Raw';
    if (m === 'raw') return 'Raw';
    return m ? m.charAt(0).toUpperCase() + m.slice(1) : '—';
  }
  if (key === 'tipo_competencia') {
    const tc = String(raw).trim().toLowerCase();
    if (tc === 'tested') return 'Tested';
    if (tc === 'untested') return 'Untested';
    return tc ? tc.charAt(0).toUpperCase() + tc.slice(1) : '—';
  }
  if (key === 'id') return String(raw);
  return String(raw).trim() || '—';
}

const RecordsPage = () => {
  const [sexo, setSexo] = useState('todos');
  const [categoriaEdad, setCategoriaEdad] = useState('todas');
  const [modalidad, setModalidad] = useState('todas');
  const [tipoCompetencia, setTipoCompetencia] = useState('todos');
  const [tipoMovimiento, setTipoMovimiento] = useState('todos');
  const [categoriaPeso, setCategoriaPeso] = useState('todas');
  const [busqueda, setBusqueda] = useState('');
  const [sortFechaRecord, setSortFechaRecord] = useState(null); // null | 'asc' | 'desc'
  const [sortKg, setSortKg] = useState(null); // null | 'asc' | 'desc'
  const [heroImage, setHeroImage] = useState(() => getFirstCachedImage(['page_hero_records']));
  const [records, setRecords] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null);
  const [paginaActual, setPaginaActual] = useState(1);

  useEffect(() => {
    getAllCMSImages().then((imgs) => setHeroImage(imgs.page_hero_records || null))
      .catch(() => setHeroImage(null));
  }, []);

  const PAGE_SIZE = 30;

  // Categorías oficiales Mujeres: 44, 48, 52, 56, 60, 67.5, 75, 82.5, 90, +90
  const rangosPeso = {
    masculino: ['52 kg', '56 kg', '60 kg', '67.5 kg', '75 kg', '82.5 kg', '90 kg', '100 kg', '110 kg', '125 kg', '140 kg', '+140 kg'],
    femenino: ['44 kg', '48 kg', '52 kg', '56 kg', '60 kg', '67.5 kg', '75 kg', '82.5 kg', '90 kg', '+90 kg']
  };

  // Si la categoría de peso es exclusiva de un sexo (ej. 82.5 kg solo Hombres), devuelve ese sexo; si es compartida (52 kg) o desconocida, null
  const getSexoExclusivoCategoria = (peso) => {
    if (!peso || peso === 'todas') return null;
    const key = normPesoParaMatch(peso);
    const inM = rangosPeso.masculino.some((p) => normPesoParaMatch(p) === key);
    const inF = rangosPeso.femenino.some((p) => normPesoParaMatch(p) === key);
    if (inM && !inF) return 'masculino';
    if (inF && !inM) return 'femenino';
    return null;
  };

  // Categorías que existen en Hombres y Mujeres (ej. 52 kg): en el desplegable usamos valor "52 kg|masculino" / "52 kg|femenino"
  const isCategoriaCompartida = (peso) => {
    const key = normPesoParaMatch(peso);
    return rangosPeso.masculino.some((p) => normPesoParaMatch(p) === key) && rangosPeso.femenino.some((p) => normPesoParaMatch(p) === key);
  };

  // Extrae peso y sexo del valor del filtro (ej. "52 kg|masculino" → { peso: "52 kg", sexoFromPeso: "masculino" })
  const parsePesoCorporalValue = (val) => {
    if (!val || val === 'todas') return { peso: 'todas', sexoFromPeso: null };
    const i = val.indexOf('|');
    if (i >= 0) return { peso: val.slice(0, i).trim(), sexoFromPeso: val.slice(i + 1).trim() || null };
    return { peso: val, sexoFromPeso: null };
  };

  // Definición de categorías de edad (Master I, II, III, IV)
  const categoriasEdad = [
    { id: 'subjunior', nombre: 'Sub-Junior', desc: 'Ej. 15-19 años' },
    { id: 'junior', nombre: 'Junior', desc: 'Ej. 24-39 años' },
    { id: 'open', nombre: 'Open', desc: 'Todas las edades' },
    { id: 'master_i', nombre: 'Master I', desc: '40-49 años' },
    { id: 'master_ii', nombre: 'Master II', desc: '50-59 años' },
    { id: 'master_iii', nombre: 'Master III', desc: '60-69 años' },
    { id: 'master_iv', nombre: 'Master IV', desc: '70+ años' }
  ];

  // Tipo de movimiento: Squat, Bench Press, Deadlift, Total
  const tiposMovimiento = [
    { id: 'todos', nombre: 'Todos' },
    { id: 'squat', nombre: 'Squat' },
    { id: 'bench_press', nombre: 'Bench Press' },
    { id: 'deadlift', nombre: 'Deadlift' },
    { id: 'total', nombre: 'Total' }
  ];

  // Cargar récords desde backend (GET /api/records). En local, el backend es API_CONFIG.BASE_URL (por defecto back-dev.achipo.cl; para BFF local usa .env REACT_APP_API_URL=http://localhost:5000).
  useEffect(() => {
    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.RECORDS.LISTAR}?_=${Date.now()}`;
    setCargando(true);
    setErrorCarga(null);
    if (process.env.NODE_ENV === 'development') {
      console.info('[Records] Cargando desde:', url);
    }
    fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`Error ${res.status} al cargar récords`);
        return res.json();
      })
      .then((data) => {
        if (data && data.verify === true) {
          setRecords([]);
          if (process.env.NODE_ENV === 'development') {
            console.warn('[Records] La respuesta es verify=1; usa la URL sin ?verify=1 para listar récords.');
          }
          return;
        }
        const list = Array.isArray(data)
          ? data
          : (data?.records ?? data?.data ?? data?.items ?? []);
        const raw = Array.isArray(list) ? list : [];
        setRecords(raw.map(normalizeRecord));
        if (process.env.NODE_ENV === 'development' && raw.length === 0) {
          console.info('[Records] Backend devolvió 0 récords. ¿Hay datos en la BD? Si usas BFF local, importa con: python -m scripts.import_records_excel --path "..." --clear');
        }
      })
      .catch((err) => {
        setErrorCarga(err?.message || 'No se pudieron cargar los récords. ¿Está el backend en marcha?');
        setRecords([]);
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Records] Error:', err?.message, '| URL:', url);
        }
      })
      .finally(() => setCargando(false));
  }, []);

  const todosLosRecords = Array.isArray(records) ? records : [];

  // Normalizar categoría para lista: "hasta 43 kg" → "43 kg"
  const normalizarCategoriaPesoParaLista = (p) => {
    const s = String(p).trim();
    const hasta = s.match(/hasta\s*(\d+\.?\d*)\s*kg/i);
    if (hasta) return hasta[1] + ' kg';
    return s.replace(/\s+/g, ' ').replace(',', '.');
  };

  // Categorías de peso que existen en los datos (Excel/API), agrupadas por sexo para que el filtro coincida con los récords
  const categoriasPesoEnDatosPorSexo = React.useMemo(() => {
    const masc = new Set();
    const fem = new Set();
    const normSexo = (s) => (s == null ? '' : String(s).trim().toLowerCase());
    todosLosRecords.forEach((r) => {
      const p = r.peso;
      if (!p || p === 'todas' || String(p).trim() === '' || String(p).trim() === '—') return;
      const pesoNorm = normalizarCategoriaPesoParaLista(String(p).trim());
      if (!pesoNorm) return;
      const s = normSexo(r.sexo);
      if (s === 'masculino' || s.includes('varon') || s.includes('masc')) masc.add(pesoNorm);
      else if (s === 'femenino' || s.includes('mujer') || s.includes('dama') || s.includes('fem')) fem.add(pesoNorm);
      else masc.add(pesoNorm); // por defecto si no está claro
    });
    const sortKey = (s) => {
      const m = String(s).match(/(\d+\.?\d*)/);
      if (m) return parseFloat(m[1]);
      if (String(s).toLowerCase().startsWith('+')) return 999;
      return 0;
    };
    return {
      masculino: Array.from(masc).sort((a, b) => sortKey(a) - sortKey(b)),
      femenino: Array.from(fem).sort((a, b) => sortKey(a) - sortKey(b)),
    };
  }, [todosLosRecords]);

  // Categorías de edad y modalidades que realmente existen en los datos (para mostrar solo opciones con récords)
  const categoriasEdadEnDatos = React.useMemo(() => {
    const set = new Set();
    todosLosRecords.forEach((r) => {
      const e = r.edad;
      if (e != null && String(e).trim()) set.add(String(e).trim().toLowerCase());
      else set.add('open');
    });
    return Array.from(set);
  }, [todosLosRecords]);
  // Normalizar valor para comparación en filtros (definido aquí para usarse en modalidadesVisibles y filtros)
  const norm = (v) => (v == null ? '' : String(v).trim().toLowerCase());
  const normPeso = (v) => {
    if (v == null) return '';
    return String(v).trim().replace(/\s+/g, ' ').replace(',', '.');
  };
  // Normaliza para comparar: "90 kg" ≠ "+90 kg", "140 kg" ≠ "+140 kg". Solo + explícito → categoría abierta.
  const normPesoParaMatch = (v) => {
    const raw = normPeso(v);
    const s = raw.toLowerCase().replace(/\s*kg\s*/gi, ' ').trim();
    const hasta = s.match(/hasta\s*(\d+\.?\d*)/);
    if (hasta) {
      const n = parseFloat(hasta[1]);
      return (Number.isInteger(n) ? String(Math.round(n)) : String(n)) + 'kg';
    }
    const tienePlus = raw.includes('+');
    if (tienePlus) {
      const numMatch = s.replace(/\+/g, ' ').trim().match(/(\d+\.?\d*)/);
      if (numMatch) {
        const num = parseFloat(numMatch[1]);
        return `+${Number.isInteger(num) ? Math.round(num) : num}kg`;
      }
    }
    const numMatch = s.match(/^(\d+\.?\d*)/);
    if (numMatch) {
      const num = parseFloat(numMatch[1]);
      return (Number.isInteger(num) ? String(Math.round(num)) : String(num)) + 'kg';
    }
    return s.replace(/\s/g, '') || '';
  };
  // Normalizar tipo de movimiento del récord para comparar con el filtro (squat, bench_press, deadlift, total)
  const normTipoMovimiento = (t) => {
    const s = (t == null ? '' : String(t).trim().toLowerCase()).replace(/\s+/g, '_');
    if (s === 'bp') return 'bench_press';
    if (s === 'dl') return 'deadlift';
    return s || '';
  };

  // Normalizar categoría de edad para comparar: Master 1, master_1, master i → master_i; etc.
  const normCategoriaEdad = (v) => {
    const s = (v == null ? '' : String(v).trim().toLowerCase()).replace(/\s+/g, '_');
    if (!s || s === 'open') return 'open';
    if (s === 'subjunior' || s === 'sub_junior') return 'subjunior';
    if (s === 'junior') return 'junior';
    if (s === 'master_i' || s === 'master_1' || s === 'masteri' || s === 'master1') return 'master_i';
    if (s === 'master_ii' || s === 'master_2' || s === 'masterii' || s === 'master2') return 'master_ii';
    if (s === 'master_iii' || s === 'master_3' || s === 'masteriii' || s === 'master3') return 'master_iii';
    if (s === 'master_iv' || s === 'master_4' || s === 'masteriv' || s === 'master4' || s === 'master_v' || s === 'master_5' || s === 'masterv' || s === 'master5') return 'master_iv';
    if (s.startsWith('master')) return 'master_i'; // "master" genérico → master_i
    return s;
  };

  // Clave numérica para ordenar: Todas < 43 < ... < 90 < +90 (categorías "+" al final)
  const sortKeyPesoOption = (s) => {
    if (s === 'todas') return -1;
    const str = String(s).trim().toLowerCase();
    if (str.startsWith('+')) return 500 + parseFloat((str.match(/(\d+\.?\d*)/) || [])[1] || 0);
    const hasta = str.match(/hasta\s*(\d+\.?\d*)/);
    const num = hasta ? parseFloat(hasta[1]) : (str.match(/(\d+\.?\d*)/) || [])[1];
    return num != null ? parseFloat(num) : 0;
  };

  // Categorías de peso que aparecen en los datos (para el filtro), ordenadas
  const categoriasPesoEnDatos = React.useMemo(() => {
    const set = new Set();
    todosLosRecords.forEach((r) => {
      const p = r.categoria_peso ?? r.peso;
      if (p != null && String(p).trim()) set.add(String(p).trim());
    });
    return Array.from(set).sort((a, b) => sortKeyPesoOption(a) - sortKeyPesoOption(b));
  }, [todosLosRecords]);

  // Listas por sexo según Excel/ACHIPO: solo categorías oficiales (Mujeres incluye 82.5, 90, +90)
  const opcionesPesoPorSexo = React.useMemo(() => ({
    masculino: [...rangosPeso.masculino].sort((a, b) => sortKeyPesoOption(a) - sortKeyPesoOption(b)),
    femenino: [...rangosPeso.femenino].sort((a, b) => sortKeyPesoOption(a) - sortKeyPesoOption(b)),
  }), []);

  // "No establecido" solo cuando faltan valor o tipo; si tiene valor (kg) y tipo (SQ/BP/DL/Total), está establecido
  const esRecordNoEstablecido = (r) => {
    const rawValor = r.valor ?? r.value ?? r.valorKg;
    const valorNum = (rawValor !== undefined && rawValor !== null && rawValor !== '')
      ? Number(String(rawValor).replace(',', '.'))
      : NaN;
    const tieneValor = !Number.isNaN(valorNum) && valorNum > 0;
    const rawTipo = r.tipo_record ?? r.tipo_record_label ?? r.tipoRecord ?? r.type ?? '';
    const tipoStr = String(rawTipo).trim().toLowerCase().replace(/\s+/g, '_');
    const tieneTipo = tipoStr.length > 0 && (
      tipoStr === 'squat' || tipoStr === 'bench_press' || tipoStr === 'bp' ||
      tipoStr === 'deadlift' || tipoStr === 'dl' || tipoStr === 'total' ||
      tipoStr.startsWith('squat') || tipoStr.startsWith('bench') || tipoStr.startsWith('dead') || tipoStr.startsWith('total')
    );
    return !(tieneValor && tieneTipo);
  };
  // Mujeres: -90 y 90+; Hombres: 90 (no -90), -140 y 140+
  // Mismo criterio que Hombres (140/-140): Mujeres 90 kg → -90, +90 kg → 90+. Depende de peso y sexo del récord.
  const formatPesoDisplay = (peso, sexo) => {
    if (peso == null || String(peso).trim() === '' || String(peso).trim() === '—') return '—';
    const s = String(peso).trim().toLowerCase();
    const esHombres = sexo === 'masculino' || (String(sexo || '').toLowerCase().includes('varon') || String(sexo || '').toLowerCase().includes('masc'));
    if (/\+/.test(s) && /\b90\b/.test(s)) return '90+';
    if (/\b90\b/.test(s) && !/\+/.test(s)) return esHombres ? '90' : '-90';
    if (/\+/.test(s) && /\b140\b/.test(s)) return '140+';
    if (/\b140\b/.test(s) && !/\+/.test(s)) return '-140';
    return peso;
  };

  // Orden: clave numérica para categoría de peso (52 kg -> 52, 67.5 kg -> 67.5, +120 kg -> 120, hasta 59 -> 59)
  const sortKeyPeso = (peso) => {
    if (peso == null || typeof peso !== 'string') return 0;
    const s = String(peso).trim().toLowerCase();
    const hasta = s.match(/hasta\s*(\d+\.?\d*)/);
    if (hasta) return parseFloat(hasta[1]) || 0;
    const plus = s.match(/\+\s*(\d+\.?\d*)/) || s.match(/(\d+\.?\d*)\s*kg/);
    if (plus) return parseFloat(plus[1]) || 0;
    const num = s.match(/(\d+\.?\d*)/);
    return num ? parseFloat(num[1]) : 0;
  };
  const sortKeyTipoRecord = (tipo) => {
    const t = (tipo ?? '').toString().trim().toLowerCase();
    if (t === 'squat' || t === 'sq') return 1;
    if (t === 'bench_press' || t === 'bp') return 2;
    if (t === 'deadlift' || t === 'dl') return 3;
    if (t === 'total') return 4;
    return 0;
  };

  // Clave para ordenar fecha_record (ej. "Noviembre 2025" -> 2025*12+11). Sin fecha válida -> 0 (va primero en asc).
  const MESES_ES = { enero: 1, febrero: 2, marzo: 3, abril: 4, mayo: 5, junio: 6, julio: 7, agosto: 8, septiembre: 9, octubre: 10, noviembre: 11, diciembre: 12 };
  const sortKeyFechaRecord = (fechaStr) => {
    if (fechaStr == null || !String(fechaStr).trim()) return 0;
    const s = String(fechaStr).trim();
    const parts = s.split(/\s+/);
    if (parts.length < 2) return 0;
    const year = parseInt(parts[parts.length - 1], 10);
    if (Number.isNaN(year)) return 0;
    const mesStr = parts.slice(0, -1).join(' ').toLowerCase();
    const month = MESES_ES[mesStr] || 0;
    return year * 12 + month;
  };

  const sexoEfectivo = sexo;

  // Filtrar records: todas las comparaciones normalizadas para que los filtros funcionen bien
  const filtrarRecords = () => {
    return todosLosRecords.filter(record => {
      if (sexoEfectivo !== 'todos') {
        if (norm(record.sexo) !== norm(sexoEfectivo)) return false;
      }
      if (categoriaEdad !== 'todas') {
        const edadRecord = record.categoria_edad ?? record.edad;
        if (normCategoriaEdad(edadRecord) !== normCategoriaEdad(categoriaEdad)) return false;
      }
      if (tipoMovimiento !== 'todos') {
        if (normTipoMovimiento(record.tipo_record) !== tipoMovimiento) return false;
      }
      if (categoriaPeso !== 'todas') {
        if (normPesoParaMatch(record.categoria_peso ?? record.peso) !== normPesoParaMatch(categoriaPeso)) return false;
      }
      if (modalidad !== 'todas') {
        const m = String(record.modalidad ?? 'raw').trim().toLowerCase().replace(/\s+/g, '_');
        const modFilter = modalidad.toLowerCase().replace(/\s+/g, '_');
        if (m !== modFilter) return false;
      }
      if (tipoCompetencia !== 'todos') {
        const tc = String(record.tipoCompetencia ?? record.tipo_competencia ?? 'untested').trim().toLowerCase();
        const tcFilter = tipoCompetencia.trim().toLowerCase();
        if (tc !== tcFilter) return false;
      }
      return true;
    });
  };

  const recordsFiltrados = React.useMemo(() => {
    let list = filtrarRecords() ?? [];
    const term = (busqueda || '').trim().toLowerCase();
    if (term) {
      list = list.filter((record) => {
        const campos = [
          record.nombre_atleta ?? record.nombre ?? '',
          record.categoria_peso ?? record.peso ?? '',
          record.lugar ?? '',
          record.fecha_record ?? record.fecha ?? '',
          record.division ?? '',
          record.categoria_edad ?? record.edad ?? '',
          record.tipo_record ?? record.tipo_record_label ?? '',
          record.kg != null ? String(record.kg) : '',
        ].map((v) => String(v).toLowerCase());
        return campos.some((c) => c.includes(term));
      });
    }
    const sortByDefault = (arr) => arr.sort((a, b) => {
      const pesoA = sortKeyPeso(a.peso);
      const pesoB = sortKeyPeso(b.peso);
      if (pesoA !== pesoB) return pesoA - pesoB;
      const tipoA = sortKeyTipoRecord(a.tipo_record);
      const tipoB = sortKeyTipoRecord(b.tipo_record);
      if (tipoA !== tipoB) return tipoA - tipoB;
      return (a.nombre || '').localeCompare(b.nombre || '');
    });
    if (sortKg === 'asc' || sortKg === 'desc') {
      const mult = sortKg === 'asc' ? 1 : -1;
      list = [...list].sort((a, b) => {
        const kgA = Number(a.kg ?? a.valor ?? 0) || 0;
        const kgB = Number(b.kg ?? b.valor ?? 0) || 0;
        if (kgA !== kgB) return mult * (kgA - kgB);
        const keyA = sortKeyFechaRecord(a.fecha_record ?? a.fecha);
        const keyB = sortKeyFechaRecord(b.fecha_record ?? b.fecha);
        if (keyA !== keyB) return keyA - keyB;
        const tipoA = sortKeyTipoRecord(a.tipo_record);
        const tipoB = sortKeyTipoRecord(b.tipo_record);
        if (tipoA !== tipoB) return tipoA - tipoB;
        return (a.nombre || '').localeCompare(b.nombre || '');
      });
    } else if (sortFechaRecord === 'asc' || sortFechaRecord === 'desc') {
      const mult = sortFechaRecord === 'asc' ? 1 : -1;
      list = [...list].sort((a, b) => {
        const keyA = sortKeyFechaRecord(a.fecha_record ?? a.fecha);
        const keyB = sortKeyFechaRecord(b.fecha_record ?? b.fecha);
        if (keyA !== keyB) return mult * (keyA - keyB);
        const pesoA = sortKeyPeso(a.peso);
        const pesoB = sortKeyPeso(b.peso);
        if (pesoA !== pesoB) return pesoA - pesoB;
        const tipoA = sortKeyTipoRecord(a.tipo_record);
        const tipoB = sortKeyTipoRecord(b.tipo_record);
        if (tipoA !== tipoB) return tipoA - tipoB;
        return (a.nombre || '').localeCompare(b.nombre || '');
      });
    } else {
      list = sortByDefault([...list]);
    }
    return list;
  }, [todosLosRecords, sexo, sexoEfectivo, categoriaEdad, tipoMovimiento, categoriaPeso, modalidad, tipoCompetencia, busqueda, sortFechaRecord, sortKg]);
  const totalPaginas = Math.max(1, Math.ceil(recordsFiltrados.length / PAGE_SIZE));
  const inicio = (paginaActual - 1) * PAGE_SIZE;
  const recordsPagina = recordsFiltrados.slice(inicio, inicio + PAGE_SIZE);
  const hayAnterior = paginaActual > 1;
  const haySiguiente = paginaActual < totalPaginas;

  // Volver a página 1 cuando cambien los filtros o los resultados
  React.useEffect(() => {
    setPaginaActual(1);
  }, [recordsFiltrados.length, sexo, categoriaEdad, tipoMovimiento, categoriaPeso, modalidad, tipoCompetencia, busqueda, sortFechaRecord, sortKg]);

  // Resetear filtros
  const resetearFiltros = () => {
    setSexo('todos');
    setCategoriaEdad('todas');
    setModalidad('todas');
    setTipoCompetencia('todos');
    setTipoMovimiento('todos');
    setCategoriaPeso('todas');
    setBusqueda('');
  };

  return (
    <div className="records-page" style={{ minHeight: '100vh', background: '#fafaf9', color: '#1c1917' }}>
      <Header />
      <PageHero
        eyebrow="Resultados"
        imageUrl={heroImage}
        title="Récords"
        subtitle="Récords nacionales de powerlifting"
      />
      <div className="records-page-wrap">
        <section className="records-mast" aria-label="Filtros">
          <div className="records-mast-header">
            <span className="records-mast-title">Filtros</span>
          </div>
          <div className="records-filters-inner records-filters-selects">
            <div className="records-filter-group">
              <label htmlFor="filter-sexo" className="records-filter-label">division (sexo)</label>
              <select id="filter-sexo" className={`records-select ${sexo !== 'todos' ? 'records-select--active' : ''}`} value={sexo} onChange={(e) => setSexo(e.target.value)} aria-label="Filtrar por división">
                <option value="todos">Todos</option>
                <option value="masculino">Hombres</option>
                <option value="femenino">Mujeres</option>
              </select>
            </div>
            <div className="records-filter-group">
              <label htmlFor="filter-edad" className="records-filter-label">CATEGORIA EDAD</label>
              <select id="filter-edad" className={`records-select records-select--small ${categoriaEdad !== 'todas' ? 'records-select--active' : ''}`} value={categoriaEdad} onChange={(e) => setCategoriaEdad(e.target.value)} aria-label="Filtrar por categoría de edad">
                <option value="todas">Todas</option>
                {categoriasEdad.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.nombre.toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div className="records-filter-group">
              <label htmlFor="filter-movimiento" className="records-filter-label">tipo record (movimiento)</label>
              <select id="filter-movimiento" className={`records-select ${tipoMovimiento !== 'todos' ? 'records-select--active' : ''}`} value={tipoMovimiento} onChange={(e) => setTipoMovimiento(e.target.value)} aria-label="Filtrar por movimiento">
                {tiposMovimiento.map(tipo => (
                  <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                ))}
              </select>
            </div>
            <div className="records-filter-group">
              <label htmlFor="filter-categoria-peso" className="records-filter-label">categoria peso</label>
              <select id="filter-categoria-peso" className={`records-select ${categoriaPeso !== 'todas' ? 'records-select--active' : ''}`} value={categoriaPeso} onChange={(e) => setCategoriaPeso(e.target.value)} aria-label="Filtrar por categoría de peso">
                <option value="todas">Todas</option>
                {categoriasPesoEnDatos.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="records-filter-group">
              <label htmlFor="filter-modalidad" className="records-filter-label">modalidad</label>
              <select id="filter-modalidad" className={`records-select ${modalidad !== 'todas' ? 'records-select--active' : ''}`} value={modalidad} onChange={(e) => setModalidad(e.target.value)} aria-label="Filtrar por modalidad">
                <option value="todas">Todas</option>
                <option value="classic_raw">Classic Raw</option>
                <option value="raw">Raw</option>
              </select>
            </div>
            <div className="records-filter-group">
              <label htmlFor="filter-tipo-competencia" className="records-filter-label">tipo competencia</label>
              <select id="filter-tipo-competencia" className={`records-select ${tipoCompetencia !== 'todos' ? 'records-select--active' : ''}`} value={tipoCompetencia} onChange={(e) => setTipoCompetencia(e.target.value)} aria-label="Filtrar por tipo de competencia">
                <option value="todos">Todos</option>
                <option value="tested">Tested</option>
                <option value="untested">Untested</option>
              </select>
            </div>
            <div className="records-search-row">
              <div className="records-search-wrap">
                <label htmlFor="records-busqueda" className="records-filter-label">Buscar</label>
                <input
                  id="records-busqueda"
                  type="search"
                  className="records-search-input"
                  placeholder="Nombre, categoría, lugar, fecha..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  aria-label="Buscar en récords"
                />
              </div>
              <button type="button" onClick={resetearFiltros} className="records-reset">Restablecer</button>
            </div>
          </div>
        </section>

        {cargando && (
          <div style={{ marginTop: '1rem' }}>
            <SkeletonRecordsTable rows={8} />
          </div>
        )}
        {!cargando && recordsFiltrados.some(esRecordNoEstablecido) && (
          <div className="records-notice">
            <strong>Récords pendientes:</strong> en las filas resaltadas la categoría aún no está homologada. Al homologarse se mostrará atleta, valor y fecha.
          </div>
        )}
        {!cargando && (
          <>
            {recordsFiltrados.length > 0 && (
              <p className="records-stats">
                {errorCarga ? <span style={{ color: '#b91c1c' }}>{errorCarga}</span> : <><strong>{recordsFiltrados.length}</strong> {recordsFiltrados.length === 1 ? 'récord' : 'récords'}</>}
              </p>
            )}
            {recordsFiltrados.length > 0 ? (
              <>
                <div className="records-table-wrap">
                  <table className="records-table">
                    <thead className="records-thead">
                      <tr>
                        {RECORDS_COLUMNS_VISIBLE.map((col) => (
                          <th key={col.key} className={col.key === 'categoria_peso' ? 'records-th--cat-peso' : col.key === 'kg' ? 'records-th--kg' : ''}>
                            {col.key === 'kg' ? (
                              <button
                                type="button"
                                className="records-th-sort"
                                onClick={() => {
                                  setSortKg((prev) => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null));
                                  setSortFechaRecord(null);
                                }}
                                title={sortKg === 'asc' ? 'Orden ascendente (clic para descendente)' : sortKg === 'desc' ? 'Orden descendente (clic para quitar)' : 'Ordenar por kg'}
                              >
                                <span className="records-th-sort-label">KG</span>
                                <span className="records-th-sort-arrows" aria-hidden>
                                  <span className={sortKg === 'asc' ? 'records-th-sort-arrow records-th-sort-arrow--active' : 'records-th-sort-arrow'} title="Ascendente">↑</span>
                                  <span className={sortKg === 'desc' ? 'records-th-sort-arrow records-th-sort-arrow--active' : 'records-th-sort-arrow'} title="Descendente">↓</span>
                                </span>
                              </button>
                            ) : col.key === 'fecha_record' ? (
                              <button
                                type="button"
                                className="records-th-sort"
                                onClick={() => {
                                  setSortFechaRecord((prev) => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null));
                                  setSortKg(null);
                                }}
                                title={sortFechaRecord === 'asc' ? 'Orden ascendente (clic para descendente)' : sortFechaRecord === 'desc' ? 'Orden descendente (clic para quitar)' : 'Ordenar por fecha'}
                              >
                                <span className="records-th-sort-label">{col.label.toUpperCase()}</span>
                                <span className="records-th-sort-arrows" aria-hidden>
                                  <span className={sortFechaRecord === 'asc' ? 'records-th-sort-arrow records-th-sort-arrow--active' : 'records-th-sort-arrow'} title="Ascendente">↑</span>
                                  <span className={sortFechaRecord === 'desc' ? 'records-th-sort-arrow records-th-sort-arrow--active' : 'records-th-sort-arrow'} title="Descendente">↓</span>
                                </span>
                              </button>
                            ) : col.key === 'categoria_peso' ? (
                              <span className="records-th-two-line">
                                <span>CAT</span>
                                <br />
                                <span>PESO</span>
                              </span>
                            ) : (
                              col.label
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {recordsPagina.map((record) => {
                        const noEstablecido = esRecordNoEstablecido(record);
                        return (
                          <tr key={record.id} className={noEstablecido ? 'records-row--unset' : ''}>
                            {RECORDS_COLUMNS_VISIBLE.map((col) => {
                              const isKg = col.key === 'kg';
                              const isMeta = ['id', 'usuario_id', 'evento_id', 'resultado_id', 'estado', 'created_at', 'updated_at'].includes(col.key);
                              const cn = ['records-cell', isKg && 'records-cell--kg', isMeta && 'records-cell--meta', col.key === 'nombre_atleta' && 'records-cell--athlete', col.key === 'categoria_peso' && 'records-cell--cat-peso'].filter(Boolean).join(' ');
                              return (
                                <td key={col.key} className={cn}>
                                  {formatRecordCell(record, col.key)}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {recordsFiltrados.length > PAGE_SIZE && (
                  <div className="records-pagination">
                    <div className="records-pagination-info">
                      {inicio + 1}–{Math.min(inicio + PAGE_SIZE, recordsFiltrados.length)} de {recordsFiltrados.length}
                    </div>
                    <div className="records-pagination-nav">
                      <button type="button" onClick={() => setPaginaActual(p => Math.max(1, p - 1))} disabled={!hayAnterior} className="records-pagination-btn">Anterior</button>
                      <span className="records-pagination-info">{paginaActual} / {totalPaginas}</span>
                      <button type="button" onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))} disabled={!haySiguiente} className="records-pagination-btn">Siguiente</button>
                    </div>
                  </div>
                )}
              </>
            ) : null}
            {recordsFiltrados.length === 0 && (
              <div className="records-empty-wrap">
                {todosLosRecords.length === 0 ? (
                  <>
                    <h3 className="records-empty-title">Sin datos</h3>
                    <p className="records-empty-text">No hay récords cargados. Se incorporan desde el panel de administración o por importación desde Excel.</p>
                  </>
                ) : (
                  <>
                    <h3 className="records-empty-title">Ningún récord coincide</h3>
                    <p className="records-empty-text">Hay {todosLosRecords.length} récords en total. Ajusta los filtros para ver resultados.</p>
                    <button type="button" onClick={resetearFiltros} className="records-pagination-btn" style={{ marginTop: '0.5rem' }}>Restablecer filtros</button>
                  </>
                )}
              </div>
            )}
          </>
        )}

        <section className="records-info-section" aria-label="Información">
          <div className="records-info-grid">
            <div className="records-info-block">
              <h4>Homologación</h4>
              <p>El récord debe realizarse en un evento oficial con jueces certificados.</p>
            </div>
            <div className="records-info-block">
              <h4>Tipos</h4>
              <p>Nacional (Chile) e Iberoamericano (países iberoamericanos).</p>
            </div>
          </div>
          <div className="records-info-actions">
            <a href="/rules" className="records-info-link">Reglamentos</a>
            <a href="/events" className="records-info-link">Eventos</a>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default RecordsPage;